import { useEffect } from 'react';
import {
  useActiveLab,
  useCurrentUser,
  useDispatch,
  useOrder,
  useOrderTaskFromTaskId,
} from '@witmetrics/data-store';
import {
  type BaseOption,
  buildOrderOption,
  buildUserOption,
} from '@witmetrics/unison-core';
import { isEmpty } from '@witmetrics/utils/misc';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import { showSuccessToaster } from '@/utils/toasters';
import { useDate } from '@/hooks/useDate';
import { useArray } from '@/hooks/useArray';
import { useString } from '@/hooks/useString';
import { useOption } from '@/hooks/useOption';
import { useInvalidFields } from '@/hooks/useInvalidFields';
import TextField from '@/components/inputs/TextField';
import Dialog, {
  DefaultDialogActions,
  DialogBody,
  DialogTitle,
} from '@/components/Dialog';
import DateTimeInput from '@/components/inputs/DateTimeInput';
import SearchableDropdown from '@/components/inputs/SearchableDropdown';
import {
  buildTaskData,
  classes,
  createNewTask,
  fetchLabUsers,
  fetchMatchingOrders,
  getInitialDate,
  searchAssignedTo,
  updateTask,
  validateFields,
} from './utils';

export type OrderTaskDialogProps = {
  taskId?: number;
  orderId?: number | null;
  onClose: () => void;
};

export default function OrderTaskDialog({
  taskId,
  orderId,
  onClose,
}: OrderTaskDialogProps) {
  const dispatch = useDispatch();
  const { onApiError, activeCalendarDate } = useAppState();
  const orderTask = useOrderTaskFromTaskId(taskId);
  const initialOrder = useOrder(orderId);
  const currentUser = useCurrentUser();
  const activeLab = useActiveLab();
  const [labUsers, setLabUsers] = useArray<BaseOption>([
    buildUserOption(currentUser!),
  ]);
  const [order, setOrder] = useOption<BaseOption>(
    initialOrder ? buildOrderOption(initialOrder) : undefined
  );
  const [assignedTo, setAssignedTo] = useOption(buildUserOption(currentUser!));
  const [dueAt, setDueAt] = useDate(
    getInitialDate(activeCalendarDate, orderTask?.task)
  );
  const [description, setDescription] = useString();
  const { setInvalidFields } = useInvalidFields();
  const [isSaving, toggleSaving] = useToggle();

  useEffect(() => {
    if (activeLab) handleFetchLabUsers();
  }, [activeLab]);

  useEffect(() => {
    if (orderTask) fillFields();
  }, [orderTask]);

  const fillFields = () => {
    const { task, order } = orderTask!;
    setDueAt(new Date(task!.dueAt));
    setOrder(buildOrderOption(order!));
    setAssignedTo(buildUserOption(task!.assignedTo));
    setLabUsers([buildUserOption(task!.assignedTo)]);
    setDescription(task!.description);
  };

  const handleSave = async () => {
    try {
      toggleSaving(true);
      const invalid = validateFields({
        dueAt,
        order,
        assignedTo,
        description,
      });
      if (!isEmpty(invalid)) return setInvalidFields(invalid);
      const data = buildTaskData({
        dueAt: dueAt!,
        assignedTo: assignedTo!,
        description,
      });
      if (taskId) {
        dispatch.addTasks([await updateTask(taskId, data)]);
        showSuccessToaster('Task updated');
      } else {
        const result = await createNewTask(order!.value, data);
        dispatch.addTasks([result.task]);
        dispatch.addOrderTasks([result.orderTask]);
        showSuccessToaster('Task created');
      }
      onClose();
    } catch (err) {
      toggleSaving(false);
      onApiError(err, 'Error saving task', () => handleSave());
    }
  };

  const isSaveDisabled = () => {
    return (
      isSaving ||
      validateFields({ dueAt, order, assignedTo, description }).length > 0
    );
  };

  const handleFetchLabUsers = async () => {
    try {
      setLabUsers(await fetchLabUsers(activeLab!.id));
    } catch (err) {
      onApiError(err, 'Error fetching lab users', () => {
        handleFetchLabUsers();
      });
    }
  };

  const handleSearchAssignedTo = async (searchQuery: string) => {
    return searchAssignedTo(searchQuery, labUsers);
  };

  return (
    <Dialog className={classes.dialog} onClose={onClose}>
      <DialogTitle
        title={taskId ? 'Edit Task' : 'New Task'}
        onClose={onClose}
      />
      <DialogBody className={classes.body}>
        <DateTimeInput
          disablePast={false}
          className={classes.input}
          label="Deadline"
          value={dueAt}
          onChange={setDueAt}
        />
        <SearchableDropdown
          className={classes.input}
          label="Assigned To"
          value={assignedTo}
          initialOptions={labUsers}
          onSearch={handleSearchAssignedTo}
          onSelect={setAssignedTo}
        />
        <SearchableDropdown
          disabled={!!taskId}
          className={classes.input}
          label="Order"
          value={order}
          initialOptions={order ? [order] : []}
          onSearch={(search) => fetchMatchingOrders(activeLab!.id, search)}
          onSelect={setOrder}
        />
        <TextField
          multiline
          className={classes.input}
          minRows={3}
          maxRows={5}
          label="Description"
          value={description}
          onChange={setDescription}
        />
      </DialogBody>
      <DefaultDialogActions
        disabled={isSaveDisabled()}
        pending={isSaving}
        confirmLabel="Save"
        pendingLabel="Saving"
        onCancel={onClose}
        onConfirm={handleSave}
      />
    </Dialog>
  );
}
